import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
// import { AddIcdCodes } from "../components/form-components/Add-ICD-Codes";
import { SelectedSectionsSummary } from "../components/form-components/InitialPsychotherapy/SelectedFormSummary";
// Outcome Measures are the same "Add Screening" popover, assessment drawer and
// submitted-screener preview the DAP note lists under Assessment.
import {
  DapScreeningButton,
  DapScreeningPreview,
} from "../components/form-components/dap-note/DapScreeningAssessments";
import { useDapScreening } from "../components/form-components/dap-note/use-dap-screening";
import PrimaryCareSoapNotePlan from "../components/form-components/primary-note/PrimaryCareSoapNotePlan";
import ProgressNoteDiagnosis from "../components/form-components/progress-note/ProgressNoteDiagnosis";
import ProgressNoteMedications from "../components/form-components/progress-note/ProgressNoteMedications";
import ProgressNoteRecommendation from "../components/form-components/progress-note/ProgressNoteRecommendation";
import {
  MentalStatusSummary,
  ProgressRiskAssessmentSummary,
} from "../components/form-components/progress-note/ProgressNoteSummaries";
import {
  OTHER_INTERVENTIONS_NOTE_KEY,
  PROGRESS_INTERVENTIONS_SECTIONS,
} from "../components/form-components/progress-note/ProgressNoteConstant";
import InterventionsUsedModal from "../components/form-components/progress-note/modals/InterventionsUsedModal";
import ProgressMentalStatusModal from "../components/form-components/progress-note/modals/ProgressMentalStatusModal";
import ProgressRiskAssessmentModal from "../components/form-components/progress-note/modals/ProgressRiskAssessmentModal";
// import RecommendationModal from "../components/form-components/progress-note/modals/RecommendationModal";

interface ProgressNoteProps {
  formik: FormikProps<Form2Payload>;
}

/**
 * Progress Note.
 *
 * Primary Diagnosis, Subjective, Objective, Outcome Measures, the whole Plan
 * section and Follow Up & Instructions reuse the components — and the formik
 * keys — the other note types use, so the notes stay in sync. The Current
 * Mental Status, Risk Assessment and Interventions Used blocks are Progress
 * Note specific and follow the same add-button → draft modal → summary flow the
 * DAP and BIRP notes use; Recommendation is edited inline instead, since it
 * does not have enough fields to be worth hiding behind a modal.
 */
const ProgressNote = ({ formik }: ProgressNoteProps) => {
  const mseDisclosure = useDisclosure();
  const riskAssessmentDisclosure = useDisclosure();
  const interventionsDisclosure = useDisclosure();
  const appointmentDisclosure = useDisclosure();
  const screening = useDapScreening();
  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Chief Complaint is captured once by the encounter header above the
            form, so the note body does not repeat it. */}

        {/* ═══════════════ PRIMARY DIAGNOSIS ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Primary Diagnosis</CustomTitle>
        </Flex>
        <Grid size={12}>
          <ProgressNoteDiagnosis formik={formik} />
        </Grid>

        {/* ═══════════════ CURRENT MENTAL STATUS ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Current Mental Status</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => mseDisclosure.open()}
            label="Add Current Mental Status"
          />
          <ProgressMentalStatusModal
            disclosureHost={mseDisclosure}
            formik={formik}
          />
          <MentalStatusSummary formik={formik} />
        </Grid>

        {/* ═══════════════ RISK ASSESSMENT ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Risk Assessment</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => riskAssessmentDisclosure.open()}
            label="Add Risk Assessment"
          />
          <ProgressRiskAssessmentModal
            disclosureHost={riskAssessmentDisclosure}
            formik={formik}
          />
          <ProgressRiskAssessmentSummary formik={formik} />
        </Grid>

        {/* ═══════════════ MEDICATIONS ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Medications</CustomTitle>
          <ProgressNoteMedications formik={formik} />
        </Grid>

        {/* ═══════════════ SUBJECTIVE REPORT ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>
            Subjective Report and Symptom Description
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            name="subjectiveFreeNote"
            placeholder="subjective information discussed in session, such as report of symptoms"
            onBlur={formik.handleBlur}
            value={formik.values?.subjectiveFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* ═══════════════ OBJECTIVE CONTENT ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Objective Content</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            rows={4}
            name="objectiveFreeNote"
            placeholder="objective information discussed during the session"
            onBlur={formik.handleBlur}
            value={formik.values?.objectiveFreeNote ?? ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* ═══════════════ OUTCOME MEASURES ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Outcome Measures</CustomTitle>
          <DapScreeningButton screening={screening} />
          <DapScreeningPreview screening={screening} />
        </Grid>

        {/* ═══════════════ INTERVENTIONS USED ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Interventions Used</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => interventionsDisclosure.open()}
            label="Add Interventions Used"
          />
          <InterventionsUsedModal
            disclosureHost={interventionsDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PROGRESS_INTERVENTIONS_SECTIONS}
            showSectionLabel={false}
            overallNoteKey={OTHER_INTERVENTIONS_NOTE_KEY}
            overallNoteLabel="Other Interventions"
          />
        </Grid>

        {/* Treatment Plan Progress used to sit here — the care plan objectives
            with a progress note against each. Dropped along with its
            `treatmentObjectiveProgressList` payload key and report block. */}

        {/* ═══════════════ ASSESSMENT ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
          <CustomTitle color={"primary"}>Assessment</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Clinical Assessment"
            rows={4}
            variant="outlined"
            name="assessmentFreeNote"
            placeholder="Clinical assessment..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <CustomTextArea
            label="Additional Notes"
            rows={3}
            name="assessmentAdditionalNotes"
            placeholder="Additional notes..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentAdditionalNotes || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {/* Assessment → additional ICD codes. Dropped: the Primary Diagnosis
            section at the top of the note already writes the same
            `assessmentCodes` list, so a second picker down here only duplicated
            it.
        <Grid item xs={12}>
          <StyleAddButton
            w="180px"
            onClick={() => assessmentIcdDisclosure.open()}
            label="Add Additional ICD Codes"
          />
          <AddIcdCodes
            formik={formik}
            disclosureHost={assessmentIcdDisclosure}
          />
        </Grid> */}

        {/* ═══════════════ PLAN ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Plan
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Plan Note"
            rows={3}
            name="planFreeNote"
            placeholder="Plan..."
            onBlur={formik.handleBlur}
            value={formik.values?.planFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <PrimaryCareSoapNotePlan
            formik={formik}
            showSendAssessment
            showAssignEducation
            showTherapies
          />
        </Grid>

        {/* ═══════════════ RECOMMENDATION ═══════════════ */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Recommendation</CustomTitle>
          <ProgressNoteRecommendation formik={formik} />
        </Grid>

        {/* ═══════════════ FOLLOW UP & INSTRUCTIONS ═══════════════ */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Follow Up & Instructions
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Follow Up & Instructions"
            rows={3}
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {hasUpdateAppointment && (
          <Grid size={12}>
            <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <StyleAddButton
                w="200px"
                onClick={() => {
                  appointmentDisclosure.open();
                }}
                label="Schedule F/U Appointment"
              />
              {appointmentDisclosure.opened && (
                <NewAppointment
                  enablePrefillPatientData
                  opened={appointmentDisclosure.opened}
                  onClose={appointmentDisclosure.close}
                />
              )}
            </Flex>
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default ProgressNote;
